import { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink, Play, Sparkles, Trophy, TriangleAlert, X } from "lucide-react";
import ProductLayout from "./sai/ProductLayout";
import Showcase from "./sai/Showcase";
import usePulse from "./sai/usePulse";
import VideoMaximize from "../../ui/VideoMaximize";
import { VIDEOS } from "./sai/DemoVideo";
import { IDS as STEP_IDS } from "./sai/ids";
import { AGES, AI, COMPANY, DEPARTMENTS, LOCATIONS, RISKS, TESTS, TIERS } from "./corporateData";
import { IDS } from "./corporateIds";
import "./Corporate.css";

// Corporate, ported from sai's CorporateScreen (brochure-demo @ 2a7c405) into
// our shell: ProductLayout renders our <Screen> via SaiProductFrame; IDs are
// data-trapezium-* via toTrapezium; "Next product" follows our STEPS.
//
// His notes: the Corporate Wellness Dashboard: a company's annual check-up
// results turned into a workforce picture for HR and leadership. The showreel
// plays first (muted, looping), then one screen, no tabs (the user,
// 2026-10-10): the dashboard on the left; on the right, the parts the live
// dashboard has today as rows (the overview, the population, where risk sits
// by department or location, and the AI insights). A row shows that part.
// Content and sample data: the dashboard's rebuild spec (2026-10). Left out,
// as the spec says: anything planned (participation rate, year-on-year trend,
// a generated report, a self-service view) and any employee-level data.
//
// Phase 3 (Harsh, 2026-10-17): "real product screenshots where possible;
// animate to highlight; front-load with autoplay video". The showreel stays the
// first block under the headline. A part's picture is now, by default, a still
// of the real dashboard taken from its walkthrough video (public/demo/corporate,
// the video's invented sample company "Acme Corp"); the redrawn dashboard stays
// one toggle away ("Redrawn sample"), with its rank-by switch. A pick glows once
// on the row and the picture frame, then rests (none under reduced motion).

const SCREEN = "corporate"; // his screen key (our product.corporate)
// The live demo of the dashboard (the user's link, 2026-10-09), opened from "Try now".
const LIVE = "https://demo-corporate.niroggyan.com";

// The dashboard's four parts: the row's words.
const PARTS = [
  { key: "overview", name: "Overview", line: "One index, three risk tiers, the two biggest risks." },
  { key: "population", name: "Population", line: "The tests most often out of range, by age group." },
  { key: "risk", name: "Where risk sits", line: "Every department and site, ranked by score." },
  { key: "ai-insights", name: "AI insights", line: "Champions, a watch list and what to do next." },
];

// Stills of the real dashboard (1184×768, cropped from the walkthrough video's
// laptop screen; its sample company, Acme Corp, is invented). [file, button, alt]
const SHOTS = {
  overview: ["Overview", "Real Corporate Wellness Dashboard, Workforce Health Overview: 48,260 employees tested, health index 76, largest risks Vitamin D (68%) and HbA1c (54%), risk tiers 58% low, 17% monitoring and 11% high, and sites ranked by wellness score."],
  groups: ["Filter by group", "Real dashboard filters: the Age group menu open on Over 45, beside Gender and Region, above the health index and the two largest-risk gauges."],
  tests: ["Test drill-down", "Real dashboard Data insights: body-system cards (nutritional 41%, renal 29%, thyroid 18% flagged abnormal) and the Health Explorer listing the Diabetes Monitoring tests ranked by abnormal rate, HbA1c first at 54.1%."],
  curve: ["Score curve", "Real dashboard Population view: the health score distribution curve across risk bands, with a hover showing 1,240 employees scoring 20 to 29."],
  locations: ["Locations", "Real dashboard Site Health Matrix: sites ranked by wellness score, Riverside Plant selected with its risk mix (41% low, 27% monitor, 24% high) and score distribution, above the age-group insights."],
  insights: ["AI action plan", "Real dashboard Recommended Action Plan, written by AI: five prioritised actions with their expected score uplift, from blood glucose for over-45s (+6.0 points) to anaemia screening for under-35s (+2.0 points)."],
};
// Each part's stills, the first shown on pick.
const PART_SHOTS = {
  overview: ["overview", "groups"],
  population: ["tests", "curve"],
  risk: ["locations"],
  "ai-insights": ["insights"],
};

const STEPS = [
  { key: "dashboard", nav: "Corporate", head: "The whole workforce, at a glance.",
    lede: "For HR and leadership. Tap a part to see it.", points: [] },
];

const pct = (n, t) => `${((n / t) * 100).toFixed(1)}%`;

// The full walkthrough's caption, shown under the player.
const WALK_CAPTION =
  "Overview, groups, locations, score curve, test drill-down, AI insights and the board-ready summary.";

const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

// The in-page player for the full walkthrough (1:25): a focus-trapped dialog
// over the page, opened from the "Watch the full walkthrough" button next to
// Try now. The video is native, with controls, and plays with sound only once
// its own click opened the dialog (the ref callback fires as the click's
// state update is committed, so the play() request still rides that click).
function WalkthroughDialog({ onClose }) {
  const panelRef = useRef(null);
  const videoRef = useRef(null);
  // The element itself, for the maximize button in the frame's corner.
  const [videoEl, setVideoEl] = useState(null);

  const setVideoRef = useCallback((el) => {
    videoRef.current = el;
    setVideoEl(el);
    if (el) el.play().catch(() => {});
  }, []);

  const close = useCallback(() => {
    videoRef.current?.pause();
    onClose();
  }, [onClose]);

  useEffect(() => {
    const panel = panelRef.current;
    const start = panel.querySelector(FOCUSABLE);
    start?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== "Tab") return;
      const focusables = Array.from(panel.querySelectorAll(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement;
      if (e.shiftKey && (activeEl === first || !panel.contains(activeEl))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (activeEl === last || !panel.contains(activeEl))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [close]);

  return (
    <>
      <div className="cw-walk-scrim" aria-hidden="true" onMouseDown={close} />
      <div
        ref={panelRef}
        className="cw-walk-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cw-walk-title"
        data-trapezium-state="walkthrough-open"
      >
        <div className="cw-walk-head">
          <h2 id="cw-walk-title">{VIDEOS.corporateExplainer.title}</h2>
          <button
            type="button"
            className="cw-walk-close"
            data-trapezium-id={IDS.closeWalkthrough}
            onClick={close}
            aria-label="Close the full walkthrough"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {/* Maximize top right; the native controls run along the bottom. */}
        <div className="cw-walk-frame">
          <video
            ref={setVideoRef}
            className="cw-walk-video"
            controls
            preload="none"
            poster={VIDEOS.corporateExplainer.wide.poster}
            src={VIDEOS.corporateExplainer.wide.src}
            aria-label={VIDEOS.corporateExplainer.title}
          />
          <VideoMaximize video={videoEl} id={IDS.maximizeWalkthrough} label="the full walkthrough" />
        </div>
        <p className="cw-walk-caption">{WALK_CAPTION}</p>
      </div>
    </>
  );
}

function Ring({ value }) {
  const r = 30; const c = 2 * Math.PI * r;
  return (
    <span className="cw-ring">
      <svg viewBox="0 0 76 76" aria-hidden="true">
        <circle cx="38" cy="38" r={r} stroke="#ECEEF0" strokeWidth="7" fill="none" />
        <circle className="cw-ring-arc" cx="38" cy="38" r={r} stroke="#10B981" strokeWidth="7" fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - value / 100)} style={{ "--c": c }} transform="rotate(-90 38 38)" />
      </svg>
      <b>{value}<small>/100</small></b>
    </span>
  );
}

function TierBar({ low, mon, high }) {
  const t = low + mon + high;
  return (
    <span className="cw-tierbar" aria-hidden="true">
      <i data-t="low" style={{ width: pct(low, t) }} /><i data-t="mon" style={{ width: pct(mon, t) }} /><i data-t="high" style={{ width: pct(high, t) }} />
    </span>
  );
}

export default function Corporate() {
  const [active, setActive] = useState(0);
  const [mode, setMode] = useState("real"); // real screenshot | drawn (the redrawn sample)
  const [shotIx, setShotIx] = useState(0);
  const [by, setBy] = useState("departments");
  const glow = usePulse(active, { ms: 1400 }); // once per pick, not on arrival
  const [walkOpen, setWalkOpen] = useState(false);
  const watchBtnRef = useRef(null);
  const rows = by === "departments" ? DEPARTMENTS : LOCATIONS;

  const closeWalkthrough = () => {
    setWalkOpen(false);
    watchBtnRef.current?.focus();
  };

  const pick = (i) => {
    if (i === active) return;
    setActive(i);
    setShotIx(0);
  };
  const shots = PART_SHOTS[PARTS[active].key];
  const shotKey = shots[Math.min(shotIx, shots.length - 1)];

  let visual;
  if (mode === "real") {
    visual = (
      <figure className="cw-shot">
        <div className="cw-shot-frame">
          <img key={shotKey} src={`/demo/corporate/${shotKey}.jpg`} alt={SHOTS[shotKey][1]} width="1184" height="768" decoding="async" />
        </div>
        <figcaption>
          {shots.length > 1 ? (
            <span className="cw-shot-pick" role="group" aria-label={`${PARTS[active].name}: screenshots`}>
              {shots.map((k, i) => (
                <button key={k} type="button" aria-pressed={k === shotKey} onClick={() => setShotIx(i)} data-trapezium-id={IDS.shot(k)}>
                  {SHOTS[k][0]}
                </button>
              ))}
            </span>
          ) : null}
          <span className="cw-shot-cap">The real dashboard · sample company</span>
        </figcaption>
      </figure>
    );
  } else if (active === 0) {
    // One card for the index, two for the risks, one for the three tiers
    // (a single bar and its legend).
    visual = (
      <div className="cw">
        <div className="cw-panel cw-index">
          <Ring value={COMPANY.index} />
          <span className="cw-index-t">
            <em>Health index</em>
            <b>Corporate Wellness Score</b>
            <span>{COMPANY.tested.toLocaleString("en-IN")} employees tested · {COMPANY.batch}</span>
          </span>
        </div>
        <div className="cw-risks">
          {RISKS.map((r, i) => (
            <div key={r.name} className="cw-panel cw-risk">
              <em>{i ? "Second largest risk" : "Largest risk"}</em>
              <b>{r.pct}%</b>
              <span>{r.name}, at risk</span>
            </div>
          ))}
        </div>
        <div className="cw-panel">
          <p className="cw-h">Risk tiers<span>{COMPANY.scored.toLocaleString("en-IN")} scored</span></p>
          <TierBar low={TIERS[0].count} mon={TIERS[1].count} high={TIERS[2].count} />
          <div className="cw-tiers">
            {TIERS.map((t) => (
              <div key={t.key} className="cw-tier">
                <span className="cw-dot" data-t={t.key} aria-hidden="true" />
                <span><b>{t.pct}%</b> {t.name}<small>{t.range}</small></span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  } else if (active === 1) {
    visual = (
      <div className="cw cw-two">
        <div className="cw-panel">
          <p className="cw-h">Top abnormal tests</p>
          {TESTS.map(([n, v], i) => (
            <div key={n} className="cw-test" style={{ "--i": i }}>
              <span>{n}</span>
              <span className="cw-bar"><i style={{ width: `${v}%` }} data-tone={v >= 41 ? "bad" : v >= 21 ? "warn" : "ok"} /></span>
              <b>{v}%</b>
            </div>
          ))}
        </div>
        <div className="cw-panel">
          <p className="cw-h">Age groups</p>
          <div className="cw-ages">
            {AGES.map((a, i) => (
              <div key={a.name} className="cw-age" data-best={i === 0 ? "true" : undefined}>
                <em>{a.name}</em><b>{a.avg}<small>/100</small></b><span>{a.high}% high risk</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  } else if (active === 2) {
    visual = (
      <div className="cw-panel cw-rank">
        <p className="cw-h">{by === "departments" ? "Department health ranking" : "Location health ranking"}<span>Average score · tier mix</span></p>
        {rows.map(([n, score, count, low, mon, high], i) => (
          <div key={n} className="cw-row" style={{ "--i": i }}>
            <span className="cw-rk">#{i + 1}</span>
            <span className="cw-rn"><b>{n}</b><span>{count} employees</span></span>
            <TierBar low={low} mon={mon} high={high} />
            <b className="cw-score" data-tone={score >= 70 ? "ok" : score >= 55 ? "warn" : "bad"}>{score}</b>
          </div>
        ))}
        <p className="cw-legend"><i data-t="low" />Low Risk <i data-t="mon" />Monitor <i data-t="high" />High Risk</p>
      </div>
    );
  } else {
    visual = (
      <div className="cw">
        <div className="cw-ai"><Sparkles size={16} aria-hidden="true" /><span><em>AI executive insight</em>{AI.summary}</span></div>
        <div className="cw-two">
          <div className="cw-note" data-tone="ok"><Trophy size={16} aria-hidden="true" /><span><em>Champions</em>{AI.champions}</span></div>
          <div className="cw-note" data-tone="warn"><TriangleAlert size={16} aria-hidden="true" /><span><em>Watch list</em>{AI.watch}</span></div>
        </div>
        <div className="cw-panel">
          <p className="cw-h">Cohort insights<span className="cw-ai-tag">AI</span></p>
          {AI.cohorts.map(([c, t, s]) => (
            <p key={c} className="cw-cohort"><span className="cw-tag">{t}</span><b>{c}</b> {s}</p>
          ))}
        </div>
      </div>
    );
  }

  const partRows = (
    <ol className="prod-rows" aria-label="Parts of the dashboard">
      {PARTS.map((p, i) => (
        <li key={p.key} style={{ "--i": i }}>
          <button type="button" aria-pressed={i === active} onClick={() => pick(i)} data-trapezium-id={STEP_IDS.step(SCREEN, p.key)}
            className={glow && i === active ? "is-glowing" : undefined}>
            <span className="prod-row-n" aria-hidden="true">{String(i + 1).padStart(2, "0")}</span>
            <span className="prod-row-t"><b>{p.name}</b><span>{p.line}</span></span>
          </button>
        </li>
      ))}
    </ol>
  );
  const viewToggle = (
    <div className="prod-ctrl cw-view" role="group" aria-label="Picture">
      {[["real", "Real screenshot"], ["drawn", "Redrawn sample"]].map(([k, l]) => (
        <button key={k} type="button" aria-pressed={mode === k} onClick={() => setMode(k)} data-trapezium-id={IDS.view(k)}>{l}</button>
      ))}
    </div>
  );
  const rankBy = mode === "drawn" && active === 2 ? (
    <div className="prod-ctrl" role="group" aria-label="Rank by">
      {[["departments", "Departments"], ["locations", "Locations"]].map(([k, l]) => (
        <button key={k} type="button" aria-pressed={by === k} onClick={() => setBy(k)} data-trapezium-id={IDS.rankBy(k)}>{l}</button>
      ))}
    </div>
  ) : null;

  return (
    <ProductLayout
      screenKey={SCREEN}
      steps={STEPS}
      active={0}
      onStep={() => {}}
      state={`step-${PARTS[active].key}`}
      visual={(
        <div
          className={`cw-visual${glow ? " is-glowing" : ""}`}
          data-trapezium-state={mode}
          data-trapezium-view={mode === "real" ? shotKey : active === 2 ? by : undefined}
        >
          {visual}
          {glow ? <span key={glow} className="cw-glow" aria-hidden="true" /> : null}
        </div>
      )}
      controls={<>{partRows}{viewToggle}{rankBy}</>}
      above={(
        <>
          <Showcase
            video={{ ...VIDEOS.corporateShowreel.wide, title: VIDEOS.corporateShowreel.title }}
            title="See the corporate dashboard in action"
            points={["One wellness score for the workforce", "Spot risk before it becomes cost", "Every region, every age group", "Every body system, at a glance", "An AI action plan"]}
            soundId={IDS.showcaseSound}
            playId={IDS.showcasePlay}
            action={(
              <>
                <a className="showcase-cta" href={LIVE} target="_blank" rel="noopener noreferrer" data-trapezium-id={IDS.tryLive}
                  aria-label="Try now: the live corporate dashboard (opens external site)">
                  Try now <ExternalLink size={16} aria-hidden="true" />
                  <span className="showcase-ext">Opens external site</span>
                </a>
                <button
                  type="button"
                  ref={watchBtnRef}
                  className="cw-watch-btn"
                  data-trapezium-id={IDS.watchWalkthrough}
                  onClick={() => setWalkOpen(true)}
                >
                  <Play size={16} fill="currentColor" aria-hidden="true" />
                  Watch the full walkthrough (1:25)
                </button>
              </>
            )}
          />
          {/* Rendered here (not as a Corporate-level sibling) so it stays inside the
              .sai-product scope that Corporate.css's selectors require; position:
              fixed lets it sit over the whole page regardless of this nesting. */}
          {walkOpen ? <WalkthroughDialog onClose={closeWalkthrough} /> : null}
        </>
      )}
      note={mode === "real"
        ? "Stills of the real dashboard, from its walkthrough video. Its sample company (Acme Corp) is invented: every name and number."
        : `Sample company (${COMPANY.name}): every name and number is invented.`}
    />
  );
}
